import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Badge, Button, ItemIdentifiers } from '@skupreme/shared/ui';

/* EN-4544: a shipment card's item rows and facts, shared by the shipment summaries and the V2 order's shipment
   before it ships (features/orders routing). */

export type ShipmentContent = {
  id: string;
  /** Absent while the line is not matched to a catalog item. */
  catalogId?: string;
  title: string;
  sku?: string;
  upc?: string;
  image?: string;
  qty: number;
  /** Every unit was cancelled: the line stays listed, marked instead of counted. */
  isCanceled?: boolean;
};

export const FULFILL_SHIPMENT = 'Fulfill Shipment';
export const FULFILL_AND_NEXT = 'Fulfill & Next';

/**
 * A shipment's main buttons: Fulfill, and Fulfill & Next while the list it was opened from has another order to
 * fulfill. The label form and the V2 routing card both use it, so the action looks the same wherever it is.
 */
export function FulfillButtons({
  label = FULFILL_SHIPMENT,
  isDisabled,
  onFulfill,
  onFulfillAndNext,
}: {
  label?: string;
  isDisabled?: boolean;
  onFulfill: () => void;
  onFulfillAndNext?: () => void;
}) {
  return (
    <div className="sk:flex sk:gap-2">
      <Button
        type="button"
        variant="primary"
        size="lg"
        className="sk:h-9 sk:flex-1"
        isDisabled={isDisabled}
        onPress={onFulfill}
      >
        {label}
      </Button>
      {onFulfillAndNext ? (
        <Button
          type="button"
          variant="pill"
          size="lg"
          className="sk:h-9 sk:flex-1"
          isDisabled={isDisabled}
          onPress={onFulfillAndNext}
        >
          {FULFILL_AND_NEXT}
        </Button>
      ) : null}
    </div>
  );
}

export function ShipmentFact({
  label,
  visual,
  value,
  detail,
}: {
  label: string;
  visual: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-1.5">
      <span className="sk:text-xs sk:text-fg-tertiary">{label}</span>
      <div className="sk:flex sk:min-w-0 sk:items-start sk:gap-2.5">
        {visual}
        <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
          <span className="sk:text-dense sk:font-semibold sk:text-fg">
            {value}
          </span>
          {detail ? (
            <span className="sk:text-xs sk:text-fg-secondary">{detail}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function ShipmentItems({ contents }: { contents: ShipmentContent[] }) {
  if (contents.length === 0) {
    return null;
  }

  return (
    <div className="sk:flex sk:flex-col">
      {contents.map(item => (
        <div
          key={item.id}
          className="sk:flex sk:min-w-0 sk:items-center sk:gap-3 sk:py-2"
        >
          {item.image ? (
            <img
              src={item.image}
              alt=""
              className="sk:size-8 sk:shrink-0 sk:rounded-option sk:object-cover sk:inset-shadow-hairline"
            />
          ) : (
            <span className="sk:size-8 sk:shrink-0 sk:rounded-option sk:bg-fill-tertiary" />
          )}

          <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-0.5">
            {item.catalogId ? (
              <Link
                to={`/app/catalogs/catalog/${encodeURIComponent(item.catalogId)}`}
                className="sk:truncate sk:text-dense sk:font-medium sk:text-link sk:hover:text-link sk:hover:underline"
                title={item.title}
              >
                {item.title}
              </Link>
            ) : (
              <span
                className="sk:truncate sk:text-dense sk:font-medium sk:text-fg"
                title={item.title}
              >
                {item.title}
              </span>
            )}
            <ItemIdentifiers
              itemTitle={item.title}
              identifiers={[
                { label: 'SKU', value: item.sku },
                { label: 'UPC', value: item.upc },
              ]}
            />
          </div>

          {item.isCanceled ? (
            <Badge tone="neutral">Cancelled</Badge>
          ) : (
            <span className="sk:shrink-0 sk:text-dense sk:text-fg-secondary">
              ×{item.qty}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
